import React, { useEffect, useRef, useState } from "react";
import { Send, CalendarCheck, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ACTION_RE = /\[\[ACTION:\s*(\{[\s\S]*?\})\s*\]\]/;
const CTA_RE = /\[\[CONSULT(?::\s*(https?:\/\/[^\]]+))?\s*\]\]/;
const DOMAIN_RE = /\b((?:https?:\/\/)?(?:www\.)?[a-z0-9][a-z0-9-]*(?:\.[a-z0-9-]+)+)(?:\/[^\s]*)?/i;

const cleanVisible = (text) => {
  let t = text.replace(ACTION_RE, "").replace(CTA_RE, "");
  const idx = t.indexOf("[[");
  return (idx >= 0 ? t.slice(0, idx) : t).trimEnd();
};

function detectDomain(msg) {
  const m = msg.match(DOMAIN_RE);
  if (!m) return null;
  const host = m[1].replace(/^https?:\/\//i, "").replace(/^www\./i, "").split("/")[0];
  // avoid matching sentences with an email or file extension noise
  if (host.includes("@")) return null;
  const tld = host.split(".").pop().toLowerCase();
  if (["png", "jpg", "js", "css", "md"].includes(tld)) return null;
  return host;
}

export const Copilot = ({ sessionId, scope, onApplyActions, onUrl, greeting, suggestions, onSignup, signupInGreeting = false, initialInput = "", onTalking }) => {
  const [messages, setMessages] = useState([{ id: 0, role: "assistant", content: "", signup: signupInGreeting }]);
  const midRef = useRef(0);
  const nextId = () => (midRef.current += 1);
  const [input, setInput] = useState(initialInput || "");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);
  const scopeRef = useRef(scope);
  scopeRef.current = scope;

  // Type the opening greeting out, one character at a time.
  useEffect(() => {
    let i = 0;
    onTalking && onTalking(true);
    const id = setInterval(() => {
      i += 1;
      setMessages((prev) => {
        const copy = [...prev];
        copy[0] = { ...copy[0], content: greeting.slice(0, i) };
        return copy;
      });
      if (i >= greeting.length) { clearInterval(id); onTalking && onTalking(false); }
    }, 14);
    return () => { clearInterval(id); onTalking && onTalking(false); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput("");

    // In the intro chat, a website triggers the scan instead of the LLM.
    if (onUrl) {
      const domain = detectDomain(msg);
      if (domain) {
        setMessages((prev) => [
          ...prev,
          { id: nextId(), role: "user", content: msg },
          { id: nextId(), role: "assistant", content: `Perfect. Scanning ${domain} now, this takes about 20 seconds while I read what is public and map your compliance landscape.` },
        ]);
        onUrl(domain);
        return;
      }
    }

    const history = messages.filter((m) => m.content);
    setMessages((prev) => [...prev, { id: nextId(), role: "user", content: msg }, { id: nextId(), role: "assistant", content: "" }]);
    setBusy(true);
    onTalking && onTalking(true);

    try {
      const resp = await fetch(`${API}/copilot`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: msg, scope: scopeRef.current, history }),
      });
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let full = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          try {
            const data = JSON.parse(line.slice(5).trim());
            if (data.delta) {
              full += data.delta;
              const shown = cleanVisible(full);
              setMessages((prev) => {
                const copy = [...prev];
                copy[copy.length - 1] = { ...copy[copy.length - 1], role: "assistant", content: shown };
                return copy;
              });
            }
          } catch (e) { /* ignore partial or malformed SSE chunk */ }
        }
      }
      const m = full.match(ACTION_RE);
      const finalShown = cleanVisible(full);
      if (m) {
        try {
          onApplyActions && onApplyActions(JSON.parse(m[1]));
        } catch (e) { /* malformed action payload, ignore */ }
      }
      const ctaMatch = full.match(CTA_RE);
      const cta = ctaMatch ? (ctaMatch[1] || "https://gdprlocal.com/contact/") : null;
      setMessages((prev) => {
        const copy = [...prev];
        copy[copy.length - 1] = { ...copy[copy.length - 1], role: "assistant", content: finalShown || "Done. I have updated your coverage.", cta };
        return copy;
      });
    } catch (e) {
      setMessages((prev) => {
        const copy = [...prev];
        copy[copy.length - 1] = { ...copy[copy.length - 1], role: "assistant", content: "Sorry, I hit a problem. You can reach a human at gdprlocal.com/contact." };
        return copy;
      });
    } finally {
      setBusy(false);
      onTalking && onTalking(false);
    }
  };

  const chips = suggestions || [];

  return (
    <div className="flex flex-col h-full min-h-0" data-testid="copilot">
      <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto thin-scroll px-4 py-4 space-y-3">
        {messages.map((m, i) => (
          <div key={m.id ?? i} className={`flex flex-col ${m.role === "user" ? "items-end" : "items-start"}`}>
            <div
              className={`max-w-[88%] rounded-2xl px-3.5 py-2.5 text-[14px] leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "text-white" : "text-asphalt"}`}
              style={m.role === "user" ? { background: "#364F6B" } : { background: "#FFFFFF", border: "1px solid #E4E7EC" }}
            >
              {m.content || (
                <span className="inline-flex gap-1 items-center">
                  <span className="typing-dot w-1.5 h-1.5 rounded-full bg-asphalt inline-block" />
                  <span className="typing-dot w-1.5 h-1.5 rounded-full bg-asphalt inline-block" />
                  <span className="typing-dot w-1.5 h-1.5 rounded-full bg-asphalt inline-block" />
                </span>
              )}
            </div>
            {m.role === "assistant" && m.cta && (
              <a href={m.cta} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1.5 btn-aqua px-3.5 py-2 rounded-lg text-[13px] font-medium" data-testid="copilot-consult-cta">
                <CalendarCheck size={14} color="#364F6B" /> Book a consultation
              </a>
            )}
            {m.role === "assistant" && m.signup && onSignup && (
              <button onClick={onSignup} className="mt-2 inline-flex items-center gap-1.5 btn-aqua px-3.5 py-2 rounded-lg text-[13px] font-medium" data-testid="copilot-signup-cta">
                Start sign up <ArrowRight size={14} color="#364F6B" />
              </button>
            )}
          </div>
        ))}
      </div>

      {chips.length > 0 && (
        <div className="px-4 pb-2 flex flex-wrap gap-2">
          {chips.map((s) => (
            <button key={s} onClick={() => send(s)} disabled={busy} className="text-[12.5px] px-3 py-1.5 rounded-full border hairline text-asphalt bg-white hover:bg-aqua-tint hover:border-aqua transition-colors disabled:opacity-50" data-testid="copilot-suggestion">
              {s}
            </button>
          ))}
        </div>
      )}

      <div className="p-3 border-t hairline flex items-center gap-2 bg-white">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder={onUrl ? "Enter your website, e.g. yourcompany.com" : "Ask about your situation..."}
          disabled={busy}
          data-testid="copilot-input"
          className="flex-1 outline-none text-[14px] text-asphalt bg-transparent px-2"
        />
        <button onClick={() => send()} disabled={busy} className="btn-aqua w-9 h-9 rounded-lg flex items-center justify-center disabled:opacity-50" data-testid="copilot-send" aria-label="Send">
          <Send size={16} color="#364F6B" />
        </button>
      </div>
    </div>
  );
};
